import { useState } from "react";
import { useTranslation } from "react-i18next";
import { QRCodeSVG } from "qrcode.react";
import { Copy, Check, QrCode } from "lucide-react";
import { useBrowserWallet } from "@nullaone/wallet-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { BrandButton } from "@/components/brand/BrandButton";
import { useToast } from "@/hooks/useToast";

export interface ReceiveDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Receive SOL / tokens into the embedded wallet. Shows the connected wallet's
 * address as a scannable QR plus the full, visually-verifiable base58 string
 * (defends against the clipboard-hijack attack that "Copy Address" alone is
 * exposed to). Read-only — never takes user input; the address comes straight
 * from the wallet store. Ported from detrust-oracle's ReceiveDialog.
 */
export function ReceiveDialog({ open, onOpenChange }: ReceiveDialogProps) {
  const { t } = useTranslation();
  const { success, error: toastError } = useToast();
  const { publicKey } = useBrowserWallet();
  const address = (publicKey as string | null) ?? "";

  const [copied, setCopied] = useState(false);

  const handleCopy = async (): Promise<void> => {
    if (!address) return;
    try {
      await navigator.clipboard.writeText(address);
      success(t("wallet.receive.copied"));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toastError(t("wallet.receive.copyFailed"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-white border-border-card">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-text-dark">
            <QrCode className="h-5 w-5 text-brand-primary" aria-hidden="true" />
            {t("wallet.receive.title")}
          </DialogTitle>
          <DialogDescription className="text-text-secondary">
            {t("wallet.receive.scanHint")}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center gap-4 py-2">
          {/* QR on a bordered tile so it keeps a quiet zone on the light theme. */}
          {address && (
            <div className="rounded-xl border border-border-card bg-white p-4">
              <QRCodeSVG
                value={address}
                size={200}
                level="M"
                marginSize={0}
                role="img"
                aria-label={t("wallet.receive.qrAlt")}
              />
            </div>
          )}

          <div className="w-full space-y-1.5">
            <p className="text-xs text-text-secondary">
              {t("wallet.receive.addressLabel")}
            </p>
            {/* Full, selectable address for visual verification — NOT truncated. */}
            <p className="select-all break-all rounded-md bg-bg-page border border-border-card px-3 py-2 font-mono text-sm text-text-dark">
              {address}
            </p>
          </div>
        </div>

        <DialogFooter>
          <BrandButton
            type="button"
            variant="ghost"
            size="default"
            onClick={() => onOpenChange(false)}
          >
            {t("wallet.receive.done")}
          </BrandButton>
          <BrandButton
            type="button"
            variant="primary"
            size="default"
            onClick={handleCopy}
          >
            {copied ? (
              <Check className="h-4 w-4 mr-2" aria-hidden="true" />
            ) : (
              <Copy className="h-4 w-4 mr-2" aria-hidden="true" />
            )}
            {copied ? t("common.copied") : t("wallet.receive.copy")}
          </BrandButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
